import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm, Alert, Table } from 'antd';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { CollapseBlock } from '../../../../common/index.jsx';
import IpPanelStoryForm from './IpPanelStoryForm.jsx';
import { useGetPanelStoryQuery, useSavePanelStoryMutation } from '../../../../../redux/services/ing_projects_panel_story';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const IpPanelStoryPage = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const { successMessage, apiErrorMessage } = useToastMessages();

  const { data: responseData, isLoading } = useGetPanelStoryQuery(projectId);
  const [savePanelStory, { isLoading: isSaving }] = useSavePanelStoryMutation();

  const panelStory = responseData?.data?.panel_story;
  const students = responseData?.data?.students || [];
  const project = responseData?.data?.project;
  const canManage = responseData?.data?.can_manage;
  const hasYear1Students = responseData?.data?.has_year1_students ?? true;
  const hasYear2Students = responseData?.data?.has_year2_students ?? true;

  useEffect(() => {
    document.title = t('Panel Story') + (project?.name ? ` - ${project.name}` : '') + ' | FRI UNIZA';
  }, [t, project?.name]);

  useEffect(() => {
    if (panelStory) {
      form.setFieldsValue({
        ...panelStory,
        ...(!hasYear1Students ? { year1_action: 'ns' } : {}),
        ...(!hasYear2Students ? { year2_action: 'ns' } : {}),
      });
    }
  }, [panelStory, hasYear1Students, hasYear2Students, form]);

  const handleSubmit = async (values) => {
    try {
      await savePanelStory({ projectId, ...values }).unwrap();
      successMessage({ message: t('Informácie o Panel Story boli úspešne uložené.') });
      navigate(-1);
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const studentColumns = [
    {
      title: t('Meno'),
      dataIndex: 'name',
      key: 'name',
      render: (text) => text || '–',
    },
    {
      title: t('Ročník'),
      dataIndex: 'year',
      key: 'year',
      render: (text) => text || '–',
    },
    {
      title: t('Študijný program'),
      dataIndex: 'study_program',
      key: 'study_program',
      render: (text) => text || '–',
    },
  ];

  return (
    <>
      <AppBarCMS
        title={t('Panel Story') + (project?.name ? ` - ${project.name}` : '')}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
      />

      <div style={{ margin: '10px' }}>
        {!canManage && !isLoading && (
          <Alert
            message={t('V tomto termíne nie je možné nastavovať informácie o prezentácii výsledkov projektu.')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <CollapseBlock
          title={t('Zoznam študentov na projekte')}
          collapsible={true}
        >
          <Table
            columns={studentColumns}
            dataSource={students}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            size="small"
          />
        </CollapseBlock>

        <IpPanelStoryForm
          onSubmit={handleSubmit}
          initials={panelStory}
          form={form}
          disabled={!canManage || isSaving}
          hasYear1Students={hasYear1Students}
          hasYear2Students={hasYear2Students}
        />
      </div>
    </>
  );
};

export default IpPanelStoryPage;
